import {
	Button,
	Col,
	Input,
	InputNumber,
	Row,
	Select,
	Tooltip,
	Result,
	Popconfirm,
	Progress,
} from "antd";
import {
	InfoCircleOutlined,
	MinusCircleOutlined,
	PlusCircleOutlined,
} from "@ant-design/icons";
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import { InvestorAPI } from "../../../Api/InvestorAPI";
import { TemplateAPI } from "../../../Api/TemplatesAPI";
import { ContractAPI } from "../../../Api/ContractAPI";
import { InstitutionsAPI } from "../../../Api/InstitutionsAPI";

import { useInvestorData } from "../../../Hooks/useInvestorData/useInvestorData";

import FormLayout from "../../FormLayout/FormLayout";
import NameField from "../../Fields/NameField/NameField";
import LastNameField from "../../Fields/LastNameField/LastNameField";
import SecondLastNameField from "../../Fields/SecondLastNameField/SecondLastNameField";
import DocumentTypeField from "../../Fields/DocumentTypeField/DocumentTypeField";
import DocumentExpirationField from "../../Fields/DocumentExpirationField/DocumentExpirationField";
import EmailField from "../../Fields/EmailField/EmailField";
import DateField from "../../Fields/DateField/DateField";
import CountryField from "../../Fields/CountryField/CountryField";
import TextField from "../../Fields/TextField/TextField";
import SelectField from "../../Fields/SelectField/SelectField";

import { IInvestorData } from "./IInvestorData";
import { PlacementAgentValues } from "../../../Models";

import { inputNumberFormatter, inputNumberParser } from "../../../Assets/Utils";
import { transferTypes } from "../../../Assets/Enums";

import "./InvestorData.scss";

const { Option } = Select;

const InvestorData = ({
	baseURL,
	baseURLExternal,
	selectedPersonType,
	translations,
	language,
	userIdInstitution,
	placementAgentVehicles,
	placementAgentActiveVehicles,
	clientId,
	setSelectedPersonType,
	onVehicleChange,
	unlockNextStep,
	isInvestor,
	contractLanguage,
	isReadOnly = false,
}: IInvestorData) => {
	const institutionsAPI = InstitutionsAPI(baseURL, baseURLExternal, clientId);
	const contractAPI = ContractAPI(baseURL, baseURLExternal, clientId);
	const investorsAPI = InvestorAPI(baseURL, baseURLExternal, clientId);
	const templatesAPI = TemplateAPI(baseURL, clientId);

  	let isCreand = userIdInstitution === 1231 || userIdInstitution === 527;

	const {
		placesService,
		placePredictions,
		getPlacePredictions,
		isPlacePredictionsLoading,
	} = usePlacesService({ apiKey: "AIzaSyCU9qZlMZtXtxKvRJi-NRNJtPJF0p7DpYo" });

	const {
		loading,
		noContent,
		contractErrors,
		investorsErrors,
		contractData,
		investorsData,
		error,
		nationalitiesList,
		placementAgentInstitutions,
		transferValues,
		mainAddressInputs,
		manualAddressVisibility,
		activeAddress,
		vehicles,
		onChangePersonType,
		isAllowedAmount,
		onChangeCommitment,
		onChangeQuote,
		saveData,
		onChangeLanguage,
		handleChangeData,
		handleSelectDataContract,
		handleSelectDataInvestors,
		handleChangeDate,
		addInvestor,
		onDeleteInvestor,
		onChangeCreandData,
		isTransfer,
		handleChangeTransferInvestorOrigin,
		handleSelectAddress,
		handleSearchAddress,
		isInEditableState,
	} = useInvestorData({
		translations,
		institutionsAPI,
		contractAPI,
		investorsAPI,
		templatesAPI,
		selectedPersonType,
		placementAgentVehicles,
		placementAgentActiveVehicles,
		userIdInstitution,
		isCreand,
		placesService,
		setSelectedPersonType,
		onVehicleChange,
		unlockNextStep,
		getPlacePredictions,
		isInvestor
	});

  	return (
    	<>
			{!noContent ? (
				<>
					<FormLayout
						translations={translations}
						loading={loading}
						title={translations.datosFirmantes}
						subTitle={translations.completarDatos}
						onClick={saveData}
						contractId={contractData.id}
						nextStep={unlockNextStep}
						enableNextStep={(contractData.id && investorsData.length > 0) ? true : false}
						forceEnableNextStep={isReadOnly}
						confirm={contractData.id ? null : translations.confirmContract }
						disabledSaveButton={
							!isInEditableState() ||
							error || contractErrors.noTemplate
						}
						hideSaveButton={isReadOnly}
					>
						<Row gutter={[16, 16]} className="row-wtdh">
							<SelectField
								className="input-field"
								value={contractData.vehicleId || undefined}
								fieldName="vehicleId"
								size="m"
								options={
									vehicles.map((x: PlacementAgentValues) => ({
										value: x.vehicleId,
										label: x.vehicleLegalName,
									}))
								}
								translations={translations}
								errors={[contractErrors]}
								handleSelect={handleSelectDataContract}
								disabled={isReadOnly || contractData.id !== 0 || vehicles.length === 1}
							/>
							{/* CREAND DATA BP AND CONTAINER */}
							{isCreand && (
								<>
									<Col xs={24} md={6} lg={6}>
										<label className="required"> BP</label>
										<InputNumber
											className="input-field"
											placeholder="BP"
											name="bp"
											value={contractData.bp}
											disabled={isReadOnly || contractData.id !== 0}
											status={contractErrors.bp || contractErrors.invalidBP ? "error" : ""}
											onChange={(e: any) => onChangeCreandData(e, "bp")}
										/>
										<span className={`error ${contractErrors.bp ? "show" : null}`}>
											{translations.completeCampo}
										</span>
										<span className={`error ${contractErrors.invalidBP ? "show" : null}`}>
											{translations.invalidBP}
										</span>
									</Col>
									<Col xs={24} md={6} lg={6}>
										<label className="required"> Container</label>
										<InputNumber
											className="input-field"
											placeholder="Container"
											name="container"
											disabled={isReadOnly || contractData.id !== 0}
											status={contractErrors.container || contractErrors.invalidContainer ? "error" : ""}
											value={contractData.container}
											onChange={(e: any) => onChangeCreandData(e, "container")}
										/>
										<span className={`error ${contractErrors.container ? "show" : null}`}>
											{translations.completeCampo}
										</span>
										<span className={`error ${contractErrors.invalidContainer ? "show" : null}`}>
											{translations.invalidContainer}
										</span>
									</Col>
								</>
							)}
						</Row>
						<Row gutter={[16, 0]} className="row-wtdh">
							<Col xs={12} md={6} lg={6}>
								<label className="required"> {translations.tipoPersona}</label>
								<Select
									className="input-field"
									value={contractData.idPersonType}
									onChange={onChangePersonType}
									disabled={isReadOnly || contractData.id !== 0}
								>
									<Option value={2}>{translations.juridica}</Option>
									<Option value={1}>{translations.fisica}</Option>
								</Select>
							</Col>
							<Col xs={12} md={6} lg={6}>
								<label className="required"> {translations.compromiso}</label>
								<InputNumber
									className="input-field"
									name="commitment"
									value={contractData.commitment}
									onBlur={(e: any) => isAllowedAmount(e.target.value)}
									onChange={onChangeCommitment}
									disabled={isReadOnly}
									status={contractErrors.commitment ? "error" : ""}
									formatter={ inputNumberFormatter }
									parser={ inputNumberParser }
								/>
								<span className={`error ${contractErrors.commitment ? "show" : null}`}>
									{translations.messageCompromiso}
								</span>
							</Col>
							<Col xs={12} md={6} lg={6}>
								<label className="required"> {translations.language}</label>
								<Select
									className="input-field"
									value={contractData.idLanguage}
									onChange={onChangeLanguage}
									disabled={isReadOnly || contractData.id !== 0}
								>
									<Option value={1}>{translations.spanish}</Option>
									<Option value={2}>{translations.english}</Option>
									<Option value={3}>{translations.portuguese}</Option> 
								</Select>
							</Col>
							<Col xs={12} md={6} lg={6}>
								<p>
									<label className="required">
										{translations.personasFirmantes}
									</label>
									<Tooltip
										placement="topLeft"
										title={translations.tootltipPersonasFirmantes}
									>
										<InfoCircleOutlined className="tooltip" />
									</Tooltip>
								</p>
								<div className="numSignaturies">
									<span>{contractData.numSignatories}</span>
									<Button type="text" className="save-btn" onClick={addInvestor} disabled={isReadOnly || contractData.id !== 0}>
										<PlusCircleOutlined />
									</Button>
								</div>
							</Col>
						</Row>
						{contractErrors.noTemplate &&
							<span className={`error-big ${contractErrors.noTemplate ? "show" : null}`}>
								{translations.errorTemplate}
							</span>
						}
						{
							isTransfer() ?
								(
									<Row justify="space-between">
										<h3>
											{translations.transfersTitle}
										</h3>
										<Row gutter={[16, 16]} className="row-wtdh transfer-inputs">
											<SelectField
												className="input-field"
												value={contractData.transferReason}
												fieldName="transferReason"
												size="m"
												options={transferTypes[language as keyof typeof transferTypes]}
												translations={translations}
												errors={[contractErrors]}
												handleSelect={handleSelectDataContract}
												disabled={isReadOnly}
											/>
											<Col xs={12} md={12} lg={12}>
												<label className="required"> {translations.transferPrice}</label>
												<InputNumber
													className="input-field"
													name="quote"
													value={contractData.quote}
													disabled={isReadOnly}
													onChange={onChangeQuote}
												/>
											</Col>
											<SelectField
												className="input-field"
												value={contractData.idInvestorOrigin}
												fieldName="idInvestorOrigin"
												size="l"
												options={placementAgentInstitutions}
												translations={translations}
												errors={[contractErrors]}
												disabled={isReadOnly || contractData.vehicleId === 0 || contractData.id !== 0}
												handleSelect={handleChangeTransferInvestorOrigin}
											/>
										</Row>
										<Row justify="center" className="row-wtdh">
											<b>
												{translations.transferAvailable}: {`€ ${
													transferValues ? transferValues.available
														.toFixed(0)
														.replace('.', ',')
														.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
													: 0}`
												}
											</b>
										</Row>
										<Row justify="center" className="row-wtdh">
											<span>
												{translations.previousTransfers}: {`€ ${
													transferValues ? transferValues.transfered
														.toFixed(0)
														.replace('.', ',')
														.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
													: 0}`
												}
											</span>
										</Row>
										<Row justify="center" className="row-wtdh">
											<Col xs={24} md={12} lg={12}>
												<Progress percent={transferValues ? ((transferValues.transfered * 100) / (transferValues.available + transferValues.transfered)) : 0} showInfo={false} />
											</Col>
										</Row>
									</Row>
								)
								: null
						}
						{/* Investors */}
						<Row className="investor-list">
							{contractData.numSignatories > 0 &&
								investorsData.map((investor: any, index: number) => (
									<div className="subitem-line">
										<h3>
											{translations.datosFirmante} {index + 1}
										</h3>
										<Row gutter={[16, 8]} justify="start">
											<NameField
												name={ investor.name }
												size="s"
												translations={ translations}
												errors={ investorsErrors }
												index={ index }
												handleChangeData={ handleChangeData }
												disabled={ isReadOnly }
											/>
											<LastNameField
												lastName={ investor.lastName }
												size="s"
												translations={ translations }
												errors={ investorsErrors }
												index={ index }
												handleChangeData={ handleChangeData }
												disabled={ isReadOnly }
											/>
											<SecondLastNameField
												lastName={ investor.secondLastName }
												size="s"
												translations={ translations }
												errors={ investorsErrors }
												index={ index }
												handleChangeData={ handleChangeData }
												required={investor.nationality === 'Portugal'}
												disabled={ isReadOnly }
											/>
											<DocumentTypeField
												value={ investor.documentType! }
												index={ index }
												translations={ translations }
												errors={ investorsErrors }
												handleSelect={ handleSelectDataInvestors }
												disabled={ isReadOnly }
											/>
											<Col xs={12} md={6} lg={6}>
												<label className="required">
													{translations.documentNumber}
													<Tooltip placement="top" title={<span>{translations.documentNumberTooltip}</span>}>
														{" "} <InfoCircleOutlined />
													</Tooltip>
												</label>
												<Input
													className="input-field"
													name="document"
													value={investor.document}
													disabled={isReadOnly}
													onChange={handleChangeData(index)}
													placeholder={translations.documentNumber}
													status={investorsErrors[index].document ? "error" : "" }
												/>
												<span className={`error ${investorsErrors[index].document ? 'show' : null}`}>{translations.completeCampo}</span>
												<span className={`error ${investorsErrors[index].documentFormat ? 'show' : null}`}>{translations.invalidIdDocument}</span>
											</Col>
											<DocumentExpirationField
												documentExpiration={investor.docValidThru}
												translations={translations}
												errors={investorsErrors}
												fieldName="docValidThru"
												index={index}
												handleDatePicker={handleChangeDate}
												disabled={ isReadOnly }	
											/>
											<EmailField
												value={investor.emailContact}
												fieldName="emailContact"
												translations={translations}
												errors={investorsErrors}
												index={index}
												handleChangeData={handleChangeData}
												disabled={ isReadOnly }
											/>
											<DateField
												value={investor.birthDate}
												fieldName="birthDate"
												required
												translations={translations}
												errors={investorsErrors}
												index={index}
												handleDatePicker={handleChangeDate}
												disabled={ isReadOnly }
											/>
											<CountryField
												value={investor.nationality}
												fieldName="nationality"
												nationalitiesList={nationalitiesList}
												translations={translations}
												errors={investorsErrors}
												index={index}
												handleSelect={handleSelectDataInvestors}
												language={language}
												contractLanguage={contractLanguage}
												disabled={ isReadOnly }
											/>
											<TextField
												size='s'
												value={investor.phonePrefix}
												fieldName="phonePrefix"
												placeholder="+34"
												translations={translations}
												required
												errors={investorsErrors}
												index={index}
												handleChange={handleChangeData}
												disabled={ isReadOnly }
											/>
											<TextField
												size='s'
												value={investor.phoneNumber}
												fieldName="phoneNumber"
												placeholder="000 000 000"
												translations={translations}
												required
												errors={investorsErrors}
												index={index}
												handleChange={handleChangeData}
												disabled={ isReadOnly }
											/>
										</Row>
										<Row gutter={[16, 8]} justify="start" className="main-residence-title">
											<Col xs={24} md={12} lg={12}>
												<label className="required"> {translations.mainResidence} </label>
												<Select
													style={{ width: "100%" }}
													showSearch
													value={mainAddressInputs[index]}
													placeholder={translations.direccion}
													disabled={isReadOnly}
													onSearch={handleSearchAddress(index, 'mainResidence')}
													onChange={handleSelectAddress(index, 'mainResidence')}
													filterOption={false} // Importante: para que solo muestre las opciones de Google
													notFoundContent={isPlacePredictionsLoading ? "Loading..." : null}
												>
													{activeAddress && activeAddress.index === index && activeAddress.field === "mainResidence"
														? placePredictions.map((item) => (
															<Option key={item.place_id} value={item.description}>
															{item.description}
															</Option>
														))
														: null
													}
												</Select>
												<span className={`error ${investorsErrors[index].mainResidence ? 'show' : null}`}>{translations.completeCampo}</span>
											</Col>
											<TextField
												size='m'
												value={investor.mainResidenceExtra}
												index={index}
												fieldName="mainResidenceExtra"
												placeholder={translations.mainResidenceExtra}
												translations={translations}
												required={false}
												errors={investorsErrors}
												handleChange={handleChangeData}
												disabled={ isReadOnly }
											/>
										</Row>
										{manualAddressVisibility[index]?.mainResidence && (
											<Row gutter={[16, 8]} justify="start">
												<TextField
													size='m'
													label={translations.direccion}
													value={investor.mainResidence}
													fieldName="mainResidence"
													errorWhenEmpty
													translations={translations}
													required
													errors={investorsErrors}
													index={index}
													handleChange={handleChangeData}
												/>
												<TextField
													size='s'
													value={investor.mainResidencePostalCode}
													fieldName="mainResidencePostalCode"
													translations={translations}
													required
													errors={investorsErrors}
													index={index}
													handleChange={handleChangeData}
												/>
												<TextField
													size='s'
													value={investor.mainResidenceCity}
													fieldName="mainResidenceCity"
													translations={translations}
													required
													errors={investorsErrors}
													index={index}
													handleChange={handleChangeData}
												/>
												<CountryField
													value={investor.mainResidenceCountry}
													fieldName="mainResidenceCountry"
													nationalitiesList={nationalitiesList}
													translations={translations}
													errors={investorsErrors}
													index={index}
													handleSelect={handleSelectDataInvestors}
													language={language}
													contractLanguage={contractLanguage}
												/>
											</Row>
										)}
										<Row gutter={[16, 16]}>
											<Col xs={12} md={6} lg={6} className="delete-investor">
												{
													index > 0 ? 
														<Popconfirm 
															title= {translations.questionDeleteInvestor}
															okText={translations.deleteInvestor}
															cancelText={translations.no}
															placement="left"
															disabled={isReadOnly || contractData.id !== 0}
															onConfirm={() => onDeleteInvestor(index)}
														>
															<Button danger type="text" disabled={isReadOnly}>
																<MinusCircleOutlined /> {translations.deleteInvestor} {index+1}
															</Button>
														</Popconfirm>
														: null
												}
											</Col>
										</Row>
									</div>
								)
							)}
						</Row>
						<Row className="data-protection">
							<strong>{translations.clausulaProteccionTitulo}</strong>
							<span>{translations.clausulaProteccion}</span>
						</Row>
					</FormLayout>
				</>
			) : (
				<div className="no-content-wrapper">
					<Result
						status="warning"
						title={translations.messageContrato}
						extra={
							<Button type="primary" key="console">
								{translations.inicio}
							</Button>
						}
					/>
				</div>
			)}
    	</>
  	);
};

export default InvestorData;
